/**
 * Infrastructure Layer - BrowserCsvFileReader
 * ブラウザ環境でのCSVファイル読み込み実装
 */

import type { CsvFileReader, ParsedCsvResult } from '../../application/medicalReservation/ports/CsvFileReader';
import { createCsvData, type CsvData } from '../../domain/medicalReservation/CsvData';
import {
  CsvParseErrorType,
  type CsvParseError,
  type Result,
  ok,
  err,
  createCsvParseError,
} from '../../domain/medicalReservation/errors';
import { FILE_CONSTRAINTS } from '../../constants/csvHeaders';

/**
 * エンコーディング検出
 */
const detectEncoding = (buffer: ArrayBuffer): { text: string; encoding: string } => {
  let text = '';
  let detectedEncoding = 'UTF-8';

  try {
    // UTF-8で試行
    const decoder = new TextDecoder('utf-8', { fatal: true });
    text = decoder.decode(buffer);
  } catch {
    // Shift-JISで試行
    const decoder = new TextDecoder('shift-jis');
    text = decoder.decode(buffer);
    detectedEncoding = 'Shift-JIS';
  }

  return { text, encoding: detectedEncoding };
};

/**
 * CSVテキストをパースする（引用符を考慮）
 */
const parseCSVLine = (line: string, delimiter: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // エスケープされた引用符
        current += '"';
        i += 2;
        continue;
      } else {
        // 引用符の開始/終了
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      // 区切り文字
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
    i++;
  }

  // 最後のフィールドを追加
  result.push(current.trim());
  return result;
};

/**
 * 区切り文字を自動判定
 */
const detectDelimiter = (firstLine: string): string => {
  const commaCount = (firstLine.match(/,/g) || []).length;
  const tabCount = (firstLine.match(/\t/g) || []).length;
  const semicolonCount = (firstLine.match(/;/g) || []).length;

  if (tabCount > commaCount && tabCount > semicolonCount) {
    return '\t';
  }
  if (semicolonCount > commaCount) {
    return ';';
  }
  return ',';
};

/**
 * CSVテキストを論理行に分割する（ダブルクォート内の改行を考慮）
 * RFC 4180準拠のCSVパース
 */
const splitCsvLines = (csvText: string): string[] => {
  const lines: string[] = [];
  let currentLine = '';
  let inQuotes = false;
  let i = 0;

  while (i < csvText.length) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // エスケープされた引用符（""）
        currentLine += '""';
        i += 2;
        continue;
      } else {
        // 引用符の開始/終了
        inQuotes = !inQuotes;
        currentLine += char;
      }
    } else if (char === '\n' && !inQuotes) {
      // 改行（引用符の外側）→ 行の終了
      if (currentLine.trim().length > 0) {
        lines.push(currentLine);
      }
      currentLine = '';
    } else if (char === '\r' && !inQuotes) {
      // CRをスキップ（CRLF対応）
      // 次がLFの場合は何もしない（LFで行終了処理される）
      // 次がLFでない場合（CR単体）は行終了として扱う
      if (nextChar !== '\n') {
        if (currentLine.trim().length > 0) {
          lines.push(currentLine);
        }
        currentLine = '';
      }
    } else {
      currentLine += char;
    }
    i++;
  }

  // 最後の行を追加
  if (currentLine.trim().length > 0) {
    lines.push(currentLine);
  }

  return lines;
};

/**
 * ブラウザ環境でのCsvFileReader実装
 */
export class BrowserCsvFileReader implements CsvFileReader {
  /**
   * ファイルをテキストとして読み込む
   */
  async readAsText(file: File): Promise<Result<string, CsvParseError>> {
    // ファイル形式チェック
    const isValidExtension = FILE_CONSTRAINTS.ALLOWED_EXTENSIONS.some(
      (ext) => file.name.toLowerCase().endsWith(ext)
    );
    if (!isValidExtension) {
      return err(
        createCsvParseError(
          CsvParseErrorType.INVALID_FORMAT,
          'CSVファイルを選択してください',
          `対応形式: ${FILE_CONSTRAINTS.ALLOWED_EXTENSIONS.join(', ')}`
        )
      );
    }

    // ファイルサイズチェック
    if (file.size > FILE_CONSTRAINTS.MAX_SIZE) {
      const maxSizeMB = FILE_CONSTRAINTS.MAX_SIZE / (1024 * 1024);
      const currentSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return err(
        createCsvParseError(
          CsvParseErrorType.FILE_TOO_LARGE,
          'ファイルサイズが大きすぎます',
          `最大サイズ: ${maxSizeMB}MB、現在のサイズ: ${currentSizeMB}MB`
        )
      );
    }

    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = () => {
        try {
          const buffer = reader.result as ArrayBuffer;
          const { text } = detectEncoding(buffer);
          resolve(ok(text));
        } catch (error) {
          resolve(
            err(
              createCsvParseError(
                CsvParseErrorType.PARSE_FAILED,
                'ファイル読み込みエラー',
                error instanceof Error ? error.message : 'Unknown error'
              )
            )
          );
        }
      };

      reader.onerror = () => {
        resolve(
          err(
            createCsvParseError(
              CsvParseErrorType.PARSE_FAILED,
              'ファイル読み込みに失敗しました'
            )
          )
        );
      };

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * CSVテキストをパースする
   */
  parseCSV(
    csvText: string,
    _fileName: string
  ): Result<ParsedCsvResult, CsvParseError> {
    const warnings: string[] = [];

    try {
      // ダブルクォート内の改行を考慮してCSV行を分割
      const lines = splitCsvLines(csvText);

      if (lines.length === 0) {
        return err(
          createCsvParseError(
            CsvParseErrorType.EMPTY_FILE,
            'CSVファイルが空です'
          )
        );
      }

      // 区切り文字を自動判定
      const delimiter = detectDelimiter(lines[0]);
      if (delimiter !== ',') {
        warnings.push(
          delimiter === '\t'
            ? 'タブ区切りファイルとして処理します'
            : 'セミコロン区切りファイルとして処理します'
        );
      }

      // ヘッダー行のパース
      const headers = parseCSVLine(lines[0], delimiter);

      // データ行のパース
      const rows: string[][] = [];
      for (let i = 1; i < lines.length; i++) {
        const parsedRow = parseCSVLine(lines[i], delimiter);

        // 列数チェック
        if (parsedRow.length !== headers.length) {
          warnings.push(
            `行${i + 1}: 列数が一致しません (期待値: ${headers.length}, 実際: ${parsedRow.length})`
          );

          // 不足している列を空文字で補完
          while (parsedRow.length < headers.length) {
            parsedRow.push('');
          }
          // 余分な列を削除
          parsedRow.splice(headers.length);
        }

        rows.push(parsedRow);
      }

      return ok({
        headers,
        rows,
        warnings,
      });
    } catch (error) {
      return err(
        createCsvParseError(
          CsvParseErrorType.PARSE_FAILED,
          'CSVパースに失敗しました',
          error instanceof Error ? error.message : 'Unknown error'
        )
      );
    }
  }

  /**
   * ファイルを読み込んでパースする（一括処理）
   */
  async readAndParse(file: File): Promise<Result<CsvData, CsvParseError>> {
    // ファイル読み込み
    const readResult = await this.readAsText(file);
    if (!readResult.ok) {
      return readResult;
    }

    // CSVパース
    const parseResult = this.parseCSV(readResult.value, file.name);
    if (!parseResult.ok) {
      return parseResult;
    }

    // CsvDataエンティティを作成
    const csvData = createCsvData(
      parseResult.value.headers,
      parseResult.value.rows,
      file.name
    );

    return ok(csvData);
  }
}

/**
 * BrowserCsvFileReaderのファクトリ関数
 */
export const createBrowserCsvFileReader = (): CsvFileReader => {
  return new BrowserCsvFileReader();
};
